/**
 * VerParecerPage — leitura e edição do parecer do SUS (F9).
 *
 * A EDIÇÃO é funcionalidade nova: a rota legada renderizava
 * `sus/editar_parecer.html`, template que nunca existiu no repositório —
 * abrir a edição respondia 500.
 */

import {
  faArrowLeft,
  faCalendar,
  faFileMedical,
  faFileWord,
  faHashtag,
  faPenToSquare,
  faSave,
  faSpinner,
  faTrashAlt,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import {
  documentoParecer,
  excluirParecer,
  formularioParecer,
  salvarParecer,
  type FormularioParecer,
} from '../../api/sus'
import { useToast } from '../../components/Toast'

export function VerParecerPage() {
  const { id } = useParams()
  const idNumerico = Number(id)
  const navegar = useNavigate()
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()
  const [editando, setEditando] = useState(false)
  const [form, setForm] = useState<FormularioParecer | null>(null)

  const { data: parecer, isLoading, error } = useQuery({
    queryKey: ['sus-parecer', idNumerico],
    queryFn: () => documentoParecer(idNumerico),
    enabled: Number.isFinite(idNumerico),
    retry: false,
  })
  const { data: formulario } = useQuery({
    queryKey: ['sus-parecer-formulario', idNumerico],
    queryFn: () => formularioParecer(idNumerico),
    enabled: editando,
    retry: false,
  })

  useEffect(() => {
    if (formulario) setForm(formulario)
  }, [formulario])

  const salvar = useMutation({
    mutationFn: () => salvarParecer(idNumerico, form!),
    onSuccess: (atualizado) => {
      clienteQuery.setQueryData(['sus-parecer', idNumerico], atualizado)
      void clienteQuery.invalidateQueries({ queryKey: ['sus-pareceres'] })
      notificar('success', 'Parecer atualizado.')
      setEditando(false)
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  const excluir = useMutation({
    mutationFn: () => excluirParecer(idNumerico),
    onSuccess: () => {
      void clienteQuery.invalidateQueries({ queryKey: ['sus-pareceres'] })
      notificar('success', 'Parecer excluído.')
      navegar('/sus')
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  if (isLoading) {
    return (
      <p className="mt-12 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando parecer…
      </p>
    )
  }
  if (error || !parecer) {
    return (
      <div className="mx-auto mt-12 max-w-xl rounded-2xl bg-white p-8 text-center shadow-lg dark:bg-dark-card">
        <FontAwesomeIcon icon={faFileMedical} className="mb-4 text-5xl text-slate-300" />
        <h1 className="mb-2 text-2xl font-semibold text-slate-700 dark:text-slate-200">
          Parecer não encontrado
        </h1>
        <p className="mb-6 text-slate-500">
          {(error as Error)?.message ?? 'O parecer não existe ou não é seu.'}
        </p>
        <Link
          to="/sus"
          className="inline-flex items-center rounded-lg bg-teal-600 px-6 py-3 font-semibold text-white hover:bg-teal-700"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Voltar
        </Link>
      </div>
    )
  }

  const entrada =
    'w-full rounded-lg border border-slate-300 px-4 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-slate-600 dark:bg-dark-bg dark:text-slate-100'

  return (
    <div className="mx-auto max-w-5xl">
      <header className="mb-6 text-center">
        <h1 className="mb-2 text-3xl font-bold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faFileMedical} className="mr-3 text-teal-600" />
          {parecer.tipo || 'Parecer Técnico'}
        </h1>
        <p className="mb-4 text-lg text-slate-600 dark:text-slate-400">
          {parecer.assunto}
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          {parecer.numero && (
            <span className="inline-flex items-center rounded-full bg-teal-100 px-4 py-2 text-sm font-medium text-teal-800 dark:bg-teal-900/40 dark:text-teal-300">
              <FontAwesomeIcon icon={faHashtag} className="mr-2" />
              {parecer.numero}
            </span>
          )}
          {parecer.criado_em && (
            <span className="inline-flex items-center rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">
              <FontAwesomeIcon icon={faCalendar} className="mr-2" />
              {parecer.criado_em}
            </span>
          )}
          {parecer.jurisdicao && (
            <span className="inline-flex items-center rounded-full bg-cyan-100 px-4 py-2 text-sm font-medium text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300">
              {parecer.jurisdicao}
            </span>
          )}
        </div>
      </header>

      <div className="mb-6 flex flex-col justify-center gap-3 sm:flex-row">
        <button
          type="button"
          onClick={() => setEditando((v) => !v)}
          className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-blue-700"
        >
          <FontAwesomeIcon icon={editando ? faXmark : faPenToSquare} className="mr-2" />
          {editando ? 'Cancelar edição' : 'Editar'}
        </button>
        <a
          href={`/auditoria-sus/exportar/${parecer.id}`}
          className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-emerald-700"
        >
          <FontAwesomeIcon icon={faFileWord} className="mr-2" />
          Exportar DOCX
        </a>
        <button
          type="button"
          onClick={() => excluir.mutate()}
          disabled={excluir.isPending}
          className="inline-flex items-center justify-center rounded-lg bg-red-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-red-700 disabled:opacity-60"
        >
          <FontAwesomeIcon
            icon={excluir.isPending ? faSpinner : faTrashAlt}
            spin={excluir.isPending}
            className="mr-2"
          />
          Excluir
        </button>
        <Link
          to="/sus"
          className="inline-flex items-center justify-center rounded-lg bg-slate-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-slate-700"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Voltar
        </Link>
      </div>

      {editando && form ? (
        <form
          className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card"
          onSubmit={(e) => {
            e.preventDefault()
            salvar.mutate()
          }}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {([
              ['assunto', 'Assunto'],
              ['destinatario', 'Destinatário'],
              ['jurisdicao', 'Jurisdição'],
            ] as const).map(([campo, rotulo]) => (
              <div key={campo}>
                <label htmlFor={campo} className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  {rotulo}
                </label>
                <input
                  id={campo}
                  name={campo}
                  type="text"
                  value={form[campo] ?? ''}
                  onChange={(e) => setForm({ ...form, [campo]: e.target.value })}
                  className={entrada}
                />
              </div>
            ))}
          </div>
          <div>
            <label htmlFor="contexto" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Contexto
            </label>
            <textarea
              id="contexto"
              name="contexto"
              rows={4}
              value={form.contexto ?? ''}
              onChange={(e) => setForm({ ...form, contexto: e.target.value })}
              className={entrada}
            />
          </div>
          <div>
            <label htmlFor="conteudo" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Conteúdo do parecer
            </label>
            <textarea
              id="conteudo"
              name="conteudo"
              rows={20}
              value={form.conteudo ?? ''}
              onChange={(e) => setForm({ ...form, conteudo: e.target.value })}
              className={`${entrada} font-mono text-sm`}
            />
          </div>
          <button
            type="submit"
            disabled={salvar.isPending}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-cyan-600 px-6 py-3 font-semibold text-white shadow-lg transition-all hover:from-teal-700 hover:to-cyan-700 disabled:opacity-60"
          >
            <FontAwesomeIcon icon={salvar.isPending ? faSpinner : faSave} spin={salvar.isPending} />
            Salvar Alterações
          </button>
        </form>
      ) : (
        <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
          <div className="bg-gradient-to-r from-teal-600 to-cyan-600 p-6">
            <h2 className="flex items-center text-xl font-bold text-white">
              <FontAwesomeIcon icon={faFileMedical} className="mr-3" />
              {parecer.tipo}
            </h2>
            {parecer.destinatario && (
              <p className="mt-1 text-teal-100">Destinatário: {parecer.destinatario}</p>
            )}
          </div>
          <div className="p-8">
            {parecer.html ? (
              <div
                className="conteudo-documento leading-relaxed text-slate-700 dark:text-slate-300"
                dangerouslySetInnerHTML={{ __html: parecer.html }}
              />
            ) : (
              <p className="py-8 text-center text-slate-500">
                {parecer.mensagem || 'O parecer ainda não tem conteúdo.'}
              </p>
            )}
          </div>
        </article>
      )}
    </div>
  )
}
